'use client';

import { useEffect, useRef, useState, type ReactNode, type RefObject } from 'react';

import { usePestanaOculta, usePrefiereMovimientoReducido } from '@/components/asistente/movimientoReducido';
import estilosFlota from '@/components/marketing/comun/flota.module.css';
import { useEsEscritorio } from '@/components/marketing/comun/useEsEscritorio';
import { VIDEO_HERO, type VideoHero } from '@/content/videoHero';

import { aplicarEscena, recogerNodos, type NodosEscena } from './aplicarEscena';
import { ControlesVideoHeroe, ProveedorCapaVideo, useCapaVideoHeroe } from './CapaVideoHeroe';
import { ESCENA_ESCRITORIO, ESCENA_MOVIL, FOTOGRAMA_POSTER, TOTAL_BUCLE, escenaHeroe, type EscenaHeroe } from './escenaHeroe';
import { ESCENARIO_HEROE } from './textosHeroe';
import { useEnPantalla } from './useEnPantalla';

type Composicion = 'escritorio' | 'movil';

interface PropiedadesEscenarioHeroe {
    composicion: Composicion;
    /** El vídeo del héroe de escritorio; solo la vista previa pasa otro (18-09). */
    video?: VideoHero;
    /** La composición pintada en el servidor con el fotograma póster. */
    children: ReactNode;
}

// Móvil: esquina inferior derecha de la tarjeta, fuera del marco escalado para que el botón no encoja.
// El de escritorio pasó al grupo de controles del vídeo, arriba a la derecha (18-09, AM-1).
const POSICION_BOTON_MOVIL = 'bottom-4 right-[14px]';

// Media de fotograma por encima de la cual la escena se aplica un fotograma sí y otro no (tablero).
const MEDIA_MAXIMA_MS = 21;

// Un paso del reloj. El tiempo solo avanza mientras corre (vive en `tiempo`, fuera de React), así que
// pausar congela el fotograma y reanudar sigue desde ahí.
function crearPaso(nodos: NodosEscena, escena: EscenaHeroe, tiempo: { current: number }) {
    let ultimo: number | null = null;
    let media = 0;
    let par = false;
    return (ahora: number) => {
        const dt = ultimo === null ? 0 : ahora - ultimo;
        ultimo = ahora;
        tiempo.current = (tiempo.current + dt / 1000) % TOTAL_BUCLE;
        media = media ? media * 0.9 + dt * 0.1 : dt;
        par = !par;
        if (media < MEDIA_MAXIMA_MS || par) aplicarEscena(nodos, escenaHeroe(tiempo.current, escena));
    };
}

/**
 * Con movimiento reducido se ve el póster (UI-SPEC §Interaction 1). Si el ajuste se activa con el
 * bucle en marcha, el reloj se para donde esté, quizá con la biblioteca a medio llenar: se vuelve
 * al póster una vez. Al montar con movimiento reducido el reloj ya está en el póster y no hace nada.
 */
function useVueltaAlPoster(raizRef: RefObject<HTMLDivElement | null>, escena: EscenaHeroe, reducido: boolean, tiempo: { current: number }) {
    useEffect(() => {
        const raiz = raizRef.current;
        if (!reducido || !raiz || tiempo.current === FOTOGRAMA_POSTER) return;
        tiempo.current = FOTOGRAMA_POSTER;
        aplicarEscena(recogerNodos(raiz), escenaHeroe(FOTOGRAMA_POSTER, escena));
    }, [raizRef, escena, reducido, tiempo]);
}

/**
 * Un único requestAnimationFrame por árbol, que escribe por refs y nunca provoca un render. El
 * reloj arranca en el póster que ya pintó el servidor: si empezara en 0, la biblioteca completa
 * se vaciaría de golpe en el primer fotograma (17-REVIEW-A WR-02).
 */
function useRelojEscena(raizRef: RefObject<HTMLDivElement | null>, escena: EscenaHeroe, animar: boolean, reducido: boolean) {
    const tiempo = useRef(FOTOGRAMA_POSTER);
    useVueltaAlPoster(raizRef, escena, reducido, tiempo);
    useEffect(() => {
        const raiz = raizRef.current;
        if (!animar || !raiz) return;
        const avanzar = crearPaso(recogerNodos(raiz), escena, tiempo);
        let id = 0;
        const paso = () => {
            avanzar(performance.now());
            id = requestAnimationFrame(paso);
        };
        id = requestAnimationFrame(paso);
        return () => cancelAnimationFrame(id);
    }, [raizRef, escena, animar]);
}

function IconoPausa({ pausado }: { pausado: boolean }) {
    return (
        <svg width="18" height="18" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
            {pausado ? <path d="M5 3.5v9l7.5-4.5z" /> : <path d="M4.5 3h2.5v10H4.5zM9 3h2.5v10H9z" />}
        </svg>
    );
}

// WCAG 2.2.2: el bucle dura 30 s, así que se puede parar. Con movimiento reducido no hay nada que
// parar (el póster no se mueve) y el botón no se muestra. Es un conmutador: el estado lo dice
// aria-pressed y el nombre no cambia, o el lector anunciaría «Reanudar animación, pulsado» (WAI-ARIA APG).
function BotonPausa({ pausado, alPulsar, posicion }: { pausado: boolean; alPulsar: () => void; posicion: string }) {
    return (
        <button
            type="button"
            aria-pressed={pausado}
            aria-label={ESCENARIO_HEROE.pausar}
            onClick={alPulsar}
            className={`absolute flex h-11 w-11 items-center justify-center rounded-ds-md border-2 border-ink bg-surface-2 text-ink motion-reduce:hidden ${posicion}`}
        >
            <IconoPausa pausado={pausado} />
        </button>
    );
}

/**
 * Escritorio (18-09): el vídeo del héroe arranca sobre el fotograma estático. La animación HTML de la
 * Fase 17 solo corre si el vídeo cae al respaldo (UI-SPEC §B.3).
 */
function IslaEscritorio({ video, children }: { video: VideoHero; children: ReactNode }) {
    const raizRef = useRef<HTMLDivElement>(null);
    const capa = useCapaVideoHeroe(video, raizRef);
    const animar = capa.animarRespaldo;
    useRelojEscena(raizRef, ESCENA_ESCRITORIO, animar, usePrefiereMovimientoReducido());

    return (
        <div
            ref={raizRef}
            data-escenario-heroe="escritorio"
            data-estado-video={capa.estadoVisible}
            className={animar ? 'relative' : `relative ${estilosFlota.quieto}`}
        >
            <ProveedorCapaVideo valor={capa.valor}>{children}</ProveedorCapaVideo>
            {/* AM-1: en escritorio la pausa (del vídeo o del respaldo) va en el grupo de arriba a la derecha. */}
            <ControlesVideoHeroe capa={capa} />
        </div>
    );
}

/** Móvil: el reloj de la Fase 17 tal cual, con su propio botón de pausa (UI-SPEC §B.2). */
function IslaMovil({ children }: { children: ReactNode }) {
    const raizRef = useRef<HTMLDivElement>(null);
    const [pausado, setPausado] = useState(false);
    const arbolVisible = !useEsEscritorio();
    const reducido = usePrefiereMovimientoReducido();
    const oculta = usePestanaOculta();
    const enPantalla = useEnPantalla(raizRef, arbolVisible);
    const animar = arbolVisible && enPantalla && !reducido && !oculta && !pausado;
    useRelojEscena(raizRef, ESCENA_MOVIL, animar, reducido);

    return (
        <div ref={raizRef} data-escenario-heroe="movil" className={animar ? 'relative' : `relative ${estilosFlota.quieto}`}>
            {children}
            <BotonPausa pausado={pausado} alPulsar={() => setPausado((p) => !p)} posicion={POSICION_BOTON_MOVIL} />
        </div>
    );
}

/**
 * Isla de cliente del escenario del héroe (D-07). Recibe la composición ya pintada en el servidor
 * como hijos, así el marcado grande del escenario no viaja como JavaScript. Cada árbol solo se mueve
 * si es el visible, está en pantalla, la pestaña se ve, no hay movimiento reducido y nadie lo ha
 * pausado; si no, queda el fotograma en el que estuviera (al principio, el póster) y nada flota.
 */
export default function EscenarioHeroe({ composicion, video = VIDEO_HERO, children }: PropiedadesEscenarioHeroe) {
    if (composicion === 'movil') return <IslaMovil>{children}</IslaMovil>;
    return <IslaEscritorio video={video}>{children}</IslaEscritorio>;
}
